// SettingsChecklistsPane.jsx

import React, { useEffect, useMemo, useRef, useState } from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { generateKey } from 'utilities/Keys';

// Services
import {
    createChecklistTemplate,
    deleteChecklistTemplate,
    subscribeToChecklistTemplates,
    updateChecklistTemplate,
} from 'services/checklists/ChecklistTemplateService';

// Components
import Button from 'components/button/Button';
import Confirm from 'components/confirm/Confirm';
import TextInput from 'components/textinput/TextInput';

// Icons
import Icon from 'svg/Icon';

// CSS
import './SettingsChecklistsPane.css';

const normalizeString = (value) => String(value || '').trim();

const createEmptyItem = (position = 0) => ({
    key: generateKey(),
    text: '',
    position,
});

const normalizeEditorItems = (items) => {
    const list = Array.isArray(items) ? items : [];

    return list.map((item, itemIndex) => ({
        key: normalizeString(item?.key) || generateKey(),
        text: String(item?.text || ''),
        position: Number.isFinite(Number(item?.position)) ? Number(item.position) : itemIndex,
    }));
};

const serializePersistedItems = (items) => JSON.stringify(
    normalizeEditorItems(items)
        .map((item) => {
            const text = normalizeString(item.text);
            if (!text) return null;

            return {
                key: item.key,
                text,
                position: item.position,
            };
        })
        .filter(Boolean)
);

/**
 * SettingsChecklistsPane
 *
 * Renders workspace checklist template management inside Settings.
 *
 * @param {object} theme - Active theme tokens for dynamic styling.
 * @param {object|null} workspace - Active workspace model.
 * @param {object|null} user - Current signed-in user.
 * @param {boolean} canEditWorkspace - Whether the current user can edit workspace settings.
 *
 * @returns {ReactNode} The rendered checklist settings pane.
 */
const SettingsChecklistsPane = React.memo(({
    theme,
    workspace,
    user,
    canEditWorkspace,
}) => {

    // ----- DEVICE DETECTION ----- //

    const isMobile = useIsMobile();

    // ----- STATE ----- //

    const [templates, setTemplates] = useState([]);
    const [selectedTemplateKey, setSelectedTemplateKey] = useState('');
    const [title, setTitle] = useState('');
    const [items, setItems] = useState([]);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [statusMessage, setStatusMessage] = useState('');

    // ----- REFERENCES ----- //

    const lastSubmittedSignatureRef = useRef('');

    // ----- MEMOS ----- //

    const workspaceKey = useMemo(() => normalizeString(workspace?.key), [workspace?.key]);
    const userKey = useMemo(() => normalizeString(user?.key), [user?.key]);

    const selectedTemplate = useMemo(() => (
        templates.find((template) => normalizeString(template?.key) === selectedTemplateKey) || null
    ), [selectedTemplateKey, templates]);

    const draftSignature = useMemo(() => JSON.stringify({
        items: serializePersistedItems(items),
        title: String(title || ''),
    }), [items, title]);

    const savedSignature = useMemo(() => JSON.stringify({
        items: serializePersistedItems(selectedTemplate?.items || []),
        title: String(selectedTemplate?.title || ''),
    }), [selectedTemplate?.items, selectedTemplate?.title]);

    const hasTemplates = templates.length > 0;

    // ----- EFFECTS ----- //

    useEffect(() => {
        if (!workspaceKey) {
            setTemplates([]);
            setSelectedTemplateKey('');
            return undefined;
        }

        return subscribeToChecklistTemplates(workspaceKey, (rows) => {
            setTemplates(Array.isArray(rows) ? rows : []);
        });
    }, [workspaceKey]);

    useEffect(() => {
        if (templates.length === 0) {
            if (selectedTemplateKey) {
                setSelectedTemplateKey('');
            }
            return;
        }

        const hasSelectedTemplate = templates.some((template) => normalizeString(template?.key) === selectedTemplateKey);
        if (!hasSelectedTemplate) {
            setSelectedTemplateKey(normalizeString(templates[0]?.key));
        }
    }, [selectedTemplateKey, templates]);

    useEffect(() => {
        if (!selectedTemplate) {
            setTitle('');
            setItems([]);
            return;
        }

        setTitle(String(selectedTemplate?.title || ''));
        setItems(normalizeEditorItems(selectedTemplate?.items || []));
        setErrorMessage('');
    }, [selectedTemplate]);

    useEffect(() => {
        if (!selectedTemplate?.key || isDeleting || isSaving) {
            return undefined;
        }

        if (draftSignature === savedSignature) {
            if (statusMessage === 'Saving...') {
                setStatusMessage('All changes saved.');
            }
            lastSubmittedSignatureRef.current = '';
            return undefined;
        }

        if (lastSubmittedSignatureRef.current === draftSignature) {
            return undefined;
        }

        const timeoutId = window.setTimeout(async () => {
            lastSubmittedSignatureRef.current = draftSignature;
            setStatusMessage('Saving...');
            setIsSaving(true);

            try {
                await updateChecklistTemplate(selectedTemplate.key, {
                    title,
                    revision: (Number(selectedTemplate?.revision) || 0) + 1,
                    items,
                });
                setStatusMessage('All changes saved.');
            } catch (error) {
                console.error('[SettingsChecklistsPane] autosave checklist template error:', error);
                setErrorMessage(error?.message || 'Could not save checklist template.');
                setStatusMessage('');
            } finally {
                setIsSaving(false);
            }
        }, 500);

        return () => window.clearTimeout(timeoutId);
    }, [draftSignature, isDeleting, isSaving, items, savedSignature, selectedTemplate, statusMessage, title]);

    // ----- HANDLERS ----- //

    const handleCreateTemplate = async () => {
        if (!canEditWorkspace || !workspaceKey || !userKey || isCreating) return;

        setErrorMessage('');
        setStatusMessage('Creating checklist template...');
        setIsCreating(true);

        try {
            const created = await createChecklistTemplate({
                workspaceKey,
                userKey,
                title: 'Checklist',
                items: [],
            });

            const nextKey = normalizeString(created?.key);
            if (nextKey) {
                setSelectedTemplateKey(nextKey);
            }
            setStatusMessage('Checklist template created.');
        } catch (error) {
            console.error('[SettingsChecklistsPane] create checklist template error:', error);
            setErrorMessage(error?.message || 'Could not create checklist template.');
            setStatusMessage('');
        } finally {
            setIsCreating(false);
        }
    };

    const handleDeleteTemplate = async () => {
        if (!canEditWorkspace || !selectedTemplate?.key || isDeleting) return;

        setDeleteConfirmOpen(false);
        setErrorMessage('');
        setStatusMessage('Deleting checklist template...');
        setIsDeleting(true);

        try {
            const deletedKey = selectedTemplate.key;
            await deleteChecklistTemplate({
                workspaceKey,
                checklistKey: deletedKey,
            });

            setSelectedTemplateKey((currentKey) => (
                currentKey === deletedKey ? '' : currentKey
            ));
            setStatusMessage('Checklist template deleted.');
        } catch (error) {
            console.error('[SettingsChecklistsPane] delete checklist template error:', error);
            setErrorMessage(error?.message || 'Could not delete checklist template.');
            setStatusMessage('');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleAddItem = () => {
        if (!selectedTemplate || isDeleting) return;

        setItems((prev) => ([
            ...normalizeEditorItems(prev),
            createEmptyItem(prev.length),
        ]));
    };

    const handleItemChange = (itemKey, updater) => {
        setItems((prev) => normalizeEditorItems(prev).map((item, itemIndex) => {
            if (item.key !== itemKey) {
                return {
                    ...item,
                    position: itemIndex,
                };
            }

            const nextItem = typeof updater === 'function'
                ? updater(item)
                : { ...item, ...updater };

            return {
                ...nextItem,
                position: itemIndex,
            };
        }));
    };

    const handleRemoveItem = (itemKey) => {
        if (isDeleting) return;

        setItems((prev) => normalizeEditorItems(prev)
            .filter((item) => item.key !== itemKey)
            .map((item, itemIndex) => ({
                ...item,
                position: itemIndex,
            })));
    };

    // ----- RENDER ----- //

    return (
        <>
            <div
                className="settings-modal-pane-header"
                style={{
                    padding: isMobile ? '6px' : '0px',
                }}
            >
                <div className="settings-modal-pane-header-left">
                    <div
                        className="settings-modal-pane-title"
                        style={{
                            color: theme.foreground,
                        }}
                    >
                        Checklists
                    </div>
                    <div
                        className="settings-modal-pane-subtitle"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Manage reusable checklist templates for this workspace.
                    </div>
                </div>

                <Button
                    iconKey="add"
                    label={isCreating ? 'Creating...' : 'Add Template'}
                    enabled={canEditWorkspace && !isCreating && !isDeleting}
                    onClick={handleCreateTemplate}
                />
            </div>

            <div className="settings-modal-pane-contents">
                <div className={`settings-checklists-pane-container ${isMobile ? 'is-mobile' : ''}`}>

                    {/* TEMPLATE LIST */}
                    <div
                        className="settings-checklists-pane-list"
                        style={{
                            borderColor: theme.backgroundHover,
                        }}
                    >
                        {hasTemplates ? templates.map((template) => {
                            const templateKey = normalizeString(template?.key);
                            const isSelected = templateKey === selectedTemplateKey;
                            const itemCount = Array.isArray(template?.items) ? template.items.length : 0;

                            return (
                                <button
                                    key={templateKey}
                                    type="button"
                                    className="settings-checklists-pane-list-row"
                                    style={{
                                        backgroundColor: isSelected ? theme.backgroundHover : 'transparent',
                                        borderColor: isSelected ? theme.backgroundHover : 'transparent',
                                    }}
                                    onClick={() => setSelectedTemplateKey(templateKey)}
                                >
                                    <div
                                        className="settings-checklists-pane-list-row-title"
                                        style={{
                                            color: theme.foreground,
                                        }}
                                    >
                                        {template?.title || 'Checklist'}
                                    </div>

                                    <div
                                        className="settings-checklists-pane-list-row-meta"
                                        style={{
                                            color: theme.foregroundMuted,
                                        }}
                                    >
                                        {itemCount} {itemCount === 1 ? 'item' : 'items'}
                                    </div>
                                </button>
                            );
                        }) : (
                            <div
                                className="settings-checklists-pane-empty"
                                style={{
                                    color: theme.foregroundMuted,
                                }}
                            >
                                No checklist templates yet.
                            </div>
                        )}
                    </div>

                    {/* EDITOR */}
                    <div
                        className="settings-checklists-pane-editor"
                        style={{
                            borderColor: theme.backgroundHover,
                        }}
                    >
                        {selectedTemplate ? (
                            <>
                                <div className="settings-checklists-pane-editor-top">
                                    <TextInput
                                        value={title}
                                        disabled={!canEditWorkspace || isDeleting}
                                        placeholder="Checklist title"
                                        onChange={(event) => setTitle(event.target.value)}
                                    />

                                    <Button
                                        iconKey="delete"
                                        label={isDeleting ? 'Deleting...' : 'Delete'}
                                        enabled={canEditWorkspace && !isDeleting && !isSaving}
                                        onClick={() => setDeleteConfirmOpen(true)}
                                    />
                                </div>

                                <div className="settings-checklists-pane-items">
                                    {items.length > 0 ? (
                                        <div className="settings-checklists-pane-items-list">
                                            {items.map((item) => (
                                                <div
                                                    key={item.key}
                                                    className="settings-checklists-pane-item-row"
                                                >
                                                    <Icon
                                                        iconKey="check_box_outline_blank"
                                                        color={theme.foregroundMuted}
                                                        width={28}
                                                        height={28}
                                                    />

                                                    <TextInput
                                                        value={item.text}
                                                        disabled={!canEditWorkspace || isDeleting}
                                                        placeholder="Checklist item"
                                                        multiline
                                                        expandable
                                                        onChange={(event) => handleItemChange(item.key, {
                                                            text: event.target.value,
                                                        })}
                                                    />

                                                    <Button
                                                        iconKey="do_not_disturb_on"
                                                        width="32px"
                                                        enabled={canEditWorkspace && !isDeleting}
                                                        onClick={() => handleRemoveItem(item.key)}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div
                                            className="settings-checklists-pane-empty"
                                            style={{
                                                color: theme.foregroundMuted,
                                            }}
                                        >
                                            No items yet. Add a step to define this checklist template.
                                        </div>
                                    )}
                                </div>

                                <button
                                    type="button"
                                    className="settings-checklists-pane-add-step"
                                    disabled={!canEditWorkspace || isDeleting}
                                    style={{
                                        color: theme.foregroundMuted,
                                    }}
                                    onClick={handleAddItem}
                                >
                                    <Icon
                                        iconKey="add"
                                        color={theme.foregroundMuted}
                                        width={16}
                                        height={16}
                                    />
                                    Add Item
                                </button>
                            </>
                        ) : (
                            <div
                                className="settings-checklists-pane-empty"
                                style={{
                                    color: theme.foregroundMuted,
                                }}
                            >
                                Select a checklist template or create a new one.
                            </div>
                        )}
                    </div>
                </div>

                {!canEditWorkspace && (
                    <div
                        className="settings-modal-note"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Only admins and owners can manage workspace checklist templates.
                    </div>
                )}

                {statusMessage && !errorMessage && (
                    <div className="settings-modal-status">{statusMessage}</div>
                )}

                {errorMessage && (
                    <div className="settings-modal-error">{errorMessage}</div>
                )}
            </div>

            <Confirm
                title="Delete Checklist Template"
                message={`Delete "${normalizeString(selectedTemplate?.title) || 'Checklist'}"? This cannot be undone.`}
                isOpen={deleteConfirmOpen}
                setOpen={setDeleteConfirmOpen}
                onConfirm={handleDeleteTemplate}
            />
        </>
    );
});

SettingsChecklistsPane.displayName = 'SettingsChecklistsPane';

export default SettingsChecklistsPane;